<template>
  <HeaderBar />

  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'invoiceList' }">{{ t('back') }}</ButtonBack>
  </ButtonBackContainer>

  <section class="carvia-container flex flex-col justify-center">
    <h2>{{ t('createInvoice') }}</h2>
    <div class="mt-4 grid grid-cols-2 gap-4">
      <SelectField class="w-full" name="type" :label="t('invoiceType')" :options="typeOptions" />
    </div>

    <div v-if="isBookingRelated">
      <Divider />
      <div class="mt-4">
        <SelectBooking />
      </div>
    </div>

    <div v-if="!isBookingRelated && !isReversal">
      <Divider />
      <div class="mt-4 grid grid-cols-2 gap-4">
        <Select
          v-model="receiverType"
          :options="receiverTypeOptions"
          :label="t('setInvoiceReceiver')"
        />
      </div>
      <div v-if="receiverType === ReceiverType.MANUALLY" class="mt-4">
        <InvoiceReceiverForm />
      </div>
      <div v-else class="mt-4 grid grid-cols-2 gap-4">
        <SelectCustomer
          :key="receiverType"
          :allowed-roles="
            receiverType === ReceiverType.LINK_AGENCY ? [UserRole.AGENCY] : [UserRole.CUSTOMER]
          "
        />
      </div>
    </div>

    <div v-if="isReversal">
      <Divider />
      <div class="mt-4 grid grid-cols-2 gap-4">
        <div class="col-start-1">
          <SelectOriginalInvoice />
        </div>
      </div>
    </div>

    <div>
      <div>
        <Divider />
        <div class="mt-4 grid grid-cols-2 gap-4">
          <InputField name="title" :label="t('invoiceTitle')" class="col-start-1" />
          <TaxProcedureSelect
            class="col-start-1"
            :model-value="formValues.taxProcedure ?? TaxProcedure.REGULAR"
            :vat-id="counterpartyVatId"
            :require-vat-id-for-reverse-charge="!isReversal"
            @update:model-value="handleTaxProcedureUpdate"
          />
        </div>
      </div>
    </div>
    <InvoicePositionForm
      :invoice-type="formValues.type"
      :tax-procedure="formValues.taxProcedure ?? TaxProcedure.REGULAR"
    />
    <Checkbox v-model="sendInvoiceOnSave" :label="t('sendInvoiceOnSave')" class="mt-6" />
    <Divider />
    <div class="flex justify-end">
      <CVButton :is-loading="isLoading" @click.prevent="onSubmit">{{ t('saveInvoice') }}</CVButton>
    </div>
  </section>
</template>

<script lang="ts" setup>
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import { InvoiceType } from '@/entities/invoices/invoice-type.enum';
import type { CreateInvoiceDto, ReceiverData } from '@/entities/invoices/invoice.entity';
import { CounterpartyType } from '@/entities/ledger/ledger-entry.entity';
import { useInvoicePdfs } from '@/hooks/use-invoice-pdf';
import { useI18n } from 'vue-i18n';
import { useCreateInvoice } from '@/queries/use-invoices';
import { useBooking } from '@/queries/use-bookings';
import { createInvoiceCreateSchema } from '@/validation/invoice.schema';
import { useForm } from 'vee-validate';
import { computed, ref, shallowRef, watch, watchEffect } from 'vue';
import InvoicePositionForm, { type PositionFormValues } from './InvoicePositionForm.vue';
import { useUiStore } from '@/stores/ui.store';
import Checkbox from '@/components/Checkbox.vue';
import Divider from '@/components/Divider.vue';
import SelectBooking from './SelectBooking.vue';
import SelectCustomer from './SelectCustomer.vue';
import Select from '@/components/Select.vue';
import InvoiceReceiverForm from './InvoiceReceiverForm.vue';
import SelectOriginalInvoice from './SelectOriginalInvoice.vue';
import { InvoiceStatus } from '@/entities/invoices/invoice-status.enum';
import router from '@/router';
import { Alert } from '@/services/alert.service';
import { useVatIdValidation } from '@/hooks/use-vat-id-validation';
import { UserRole } from '@carvia/ros-client-types';
import TaxProcedureSelect from '@/components/TaxProcedureSelect.vue';
import { TaxProcedure } from '@carvia/ros-client-types';
import { isNonStandardTaxProcedure } from '@/utils/tax-procedure';
import { toTypedSchema } from '@vee-validate/yup';

interface InvoiceFormValues {
  title: string;
  customerId?: string;
  bookingId?: string;
  counterpartyType?: CounterpartyType;
  receiverData?: ReceiverData;
  positions: PositionFormValues[];
  type: InvoiceType;
  originalInvoiceId?: string;
  taxProcedure: TaxProcedure;
}

enum ReceiverType {
  MANUALLY = 'MANUALLY',
  LINK_CUSTOMER = 'LINK_CUSTOMER',
  LINK_AGENCY = 'LINK_AGENCY',
}

const { t } = useI18n();
const uiStore = useUiStore();
const { isValidatingVatId, validateVatId } = useVatIdValidation();

const sendInvoiceOnSave = ref(true);
const receiverType = ref(ReceiverType.MANUALLY);

const receiverTypeOptions = computed(() =>
  Object.values(ReceiverType).map((value) => ({
    label: t(value),
    value,
  })),
);

const typeOptions = computed(() =>
  Object.values([
    InvoiceType.GENERIC,
    InvoiceType.CAR_SALE,
    InvoiceType.COUPON,
    InvoiceType.SELF_BILLING,
    InvoiceType.DAMAGE,
    InvoiceType.CANCELLATION_FEE,
    InvoiceType.ADMINISTRATIVE_OFFENCE,
    InvoiceType.OTHER,
    InvoiceType.REVERSAL,
  ]).map((type) => ({
    label: `${t(`invoiceTypes.${type}`)}${bookingRelated(type) ? ` (${t('bookingRelated')})` : ''}`,
    value: type,
  })),
);

const bookingRelated = (type: InvoiceType) => {
  return (
    type === InvoiceType.DAMAGE ||
    type === InvoiceType.CANCELLATION_FEE ||
    type === InvoiceType.ADMINISTRATIVE_OFFENCE ||
    type === InvoiceType.OTHER
  );
};

const isBookingRelated = computed(() => bookingRelated(formValues.type));
const isReversal = computed(() => formValues.type === InvoiceType.REVERSAL);

const { mutateAsync: createInvoice, isPending: isCreating } = useCreateInvoice();
const { sendInvoiceToCustomer, isSending } = useInvoicePdfs();

const isLoading = computed(() => isCreating.value || isSending.value || isValidatingVatId.value);

const validationSchema = shallowRef(toTypedSchema(createInvoiceCreateSchema()));

const {
  handleSubmit,
  values: formValues,
  setFieldValue,
  setValues,
} = useForm<InvoiceFormValues>({
  initialValues: getEmptyFormValues(),
  validationSchema,
});

const selectedBookingId = computed(() => formValues.bookingId);
const { data: selectedBooking } = useBooking(
  selectedBookingId,
  computed(() => isBookingRelated.value),
);

const counterpartyVatId = computed(() => {
  if (formValues.receiverData?.vatId) {
    return formValues.receiverData.vatId;
  }
  if (isBookingRelated.value && selectedBooking.value) {
    if (formValues.counterpartyType === CounterpartyType.AGENCY) {
      return selectedBooking.value.agency?.vatId;
    }
    return selectedBooking.value.customerData?.vatId;
  }
  return undefined;
});

const counterpartyCountry = computed(() => {
  if (formValues.receiverData?.country) {
    return formValues.receiverData.country;
  }
  if (isBookingRelated.value && selectedBooking.value) {
    if (formValues.counterpartyType === CounterpartyType.AGENCY) {
      return selectedBooking.value.agency?.country;
    }
    return selectedBooking.value.customerData?.country;
  }
  return undefined;
});

watchEffect(() => {
  validationSchema.value = toTypedSchema(
    createInvoiceCreateSchema({
      counterpartyVatId,
    }),
  );
});

watch(receiverType, () => {
  formValues.receiverData = undefined;
  setFieldValue('customerId', undefined);
});
const handleTaxProcedureUpdate = (value: TaxProcedure) => {
  setFieldValue('taxProcedure', value, false);
};

watch(
  () => formValues.taxProcedure,
  (taxProcedure, previousTaxProcedure) => {
    if (formValues.positions.length === 0) {
      return;
    }
    if (isNonStandardTaxProcedure(taxProcedure)) {
      setValues(
        {
          positions: formValues.positions.map((position) => ({ ...position, tax: 0 })),
        },
        false,
      );
    } else if (previousTaxProcedure && isNonStandardTaxProcedure(previousTaxProcedure)) {
      setValues(
        {
          positions: formValues.positions.map((position) => ({ ...position, tax: 19 })),
        },
        false,
      );
    }
  },
);

const onSubmit = handleSubmit(async (values) => {
  const vatIdToValidate = values.receiverData?.vatId ?? counterpartyVatId.value;
  const countryToValidate = values.receiverData?.country ?? counterpartyCountry.value;
  if (!(await validateVatId(vatIdToValidate, undefined, countryToValidate))) return;

  const alertResult = await Alert.fire({
    titleText: t('reallyCreateInvoiceTitle'),
    text: t('reallyCreateInvoiceText'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('confirmCreateInvoice'),
    denyButtonText: t('cancel'),
  });

  if (alertResult.isConfirmed) {
    const newInvoice: CreateInvoiceDto = {
      ...values,
      positions: values.positions.map((position) => ({
        title: position.title,
        amount: position.amount ?? 0,
        unitPrice: position.unitPrice ?? 0,
        tax: (position.tax ?? 0) / 100,
      })),
      status: InvoiceStatus.OPEN,
    };
    saveInvoice(newInvoice, sendInvoiceOnSave.value);
  }
});

const saveInvoice = async (newInvoice: CreateInvoiceDto, sendInvoiceOnSave: boolean) => {
  const { id } = await createInvoice(newInvoice);
  if (sendInvoiceOnSave) {
    await sendInvoiceToCustomer(id);
  }
  router.push({ name: 'invoice', params: { id } });
};

function getEmptyFormValues(): InvoiceFormValues {
  return {
    title: '',
    customerId: undefined,
    bookingId: undefined,
    counterpartyType: undefined,
    receiverData: undefined,
    positions: [],
    type: InvoiceType.GENERIC,
    originalInvoiceId: undefined,
    taxProcedure: TaxProcedure.REGULAR,
  };
}

watchEffect(() => {
  uiStore.setHeaderTitle(t('createInvoice'));
});
</script>

<i18n lang="json">
{
  "en": {
    "createInvoice": "Create Invoice",
    "invoiceType": "Invoice Type",
    "invoiceDate": "Invoice Date",
    "originalInvoice": "Original Invoice",
    "selectOriginalInvoice": "- Select Original Invoice -",
    "title": "Title",
    "saveInvoice": "Save Invoice",
    "selectPayment": "- Select Payment -",
    "payment": "Payment",
    "repayment": "Repayment",
    "addPayment": "+ Add Payment",
    "sendInvoiceOnSave": "Automatically send Invoice to Customer after Saving",
    "compensationInvoice": "This is a damage compensation Invoice",
    "invoiceTitle": "Invoice Title",
    "paymentsMade": "Payments made",
    "administrativeOffencePositionTitle": "Processing fee administrative offence booking file number ",
    "bookingRelated": "booking related",
    "MANUALLY": "Type in invoice receiver",
    "LINK_CUSTOMER": "Link a customer",
    "LINK_AGENCY": "Link an agency",
    "setInvoiceReceiver": "Set invoice receiver",
    "reallyCreateInvoiceTitle": "Really create invoice?",
    "reallyCreateInvoiceText": "This will generate a sequential invoice number. The invoice can not be deleted.",
    "confirmCreateInvoice": "Create Invoice"
  },
  "de": {
    "createInvoice": "Rechnung erstellen",
    "invoiceType": "Rechnungstyp",
    "originalInvoice": "Originalrechnung",
    "selectOriginalInvoice": "- Originalrechnung auswählen -",
    "title": "Titel",
    "saveInvoice": "Rechnung speichern",
    "selectPayment": "- Zahlung auswählen -",
    "payment": "Zahlung",
    "repayment": "Rückzahlung",
    "addPayment": "+ Zahlung hinzufügen",
    "sendInvoiceOnSave": "Rechnung beim Speichern automatisch an Kunden senden",
    "compensationInvoice": "Dies ist eine Schadensersatzrechnung",
    "invoiceTitle": "Rechnungstitel",
    "paymentsMade": "Getätigte Zahlungen",
    "administrativeOffencePositionTitle": "Bearbeitungsgebühr Ordnungswidrigkeit Buchung Aktenzeichen ",
    "bookingRelated": "Buchungsbezogen",
    "MANUALLY": "Tippe einen Rechnungsempfänger ein",
    "LINK_CUSTOMER": "Verknüpfe einen Kunden",
    "LINK_AGENCY": "Verknüpfe einen Vermittler",
    "setInvoiceReceiver": "Rechnungsempfänger angeben",
    "reallyCreateInvoiceTitle": "Rechnung wirklich erstellen?",
    "reallyCreateInvoiceText": "Dadurch wird eine fortlaufende Rechnungsnummer erzeugt. Die Rechnung kann nicht gelöscht werden.",
    "confirmCreateInvoice": "Rechnung erstellen"
  },
  "es": {
    "createInvoice": "Crear factura",
    "invoiceType": "Tipo de factura",
    "invoiceDate": "Fecha de factura",
    "originalInvoice": "Factura original",
    "selectOriginalInvoice": "- Seleccionar factura original -",
    "title": "Título",
    "saveInvoice": "Guardar factura",
    "selectPayment": "- Seleccionar pago -",
    "payment": "Pago",
    "repayment": "Reembolso",
    "addPayment": "+ Añadir pago",
    "sendInvoiceOnSave": "Enviar automáticamente la factura al cliente al guardar",
    "compensationInvoice": "Es una factura de indemnización por daños",
    "invoiceTitle": "Título de la factura",
    "paymentsMade": "Pagos realizados",
    "administrativeOffencePositionTitle": "Gastos de tramitación infracción administrativa reserva n.º ",
    "bookingRelated": "relacionado con la reserva",
    "MANUALLY": "Introducir datos del destinatario",
    "LINK_CUSTOMER": "Vincular un cliente",
    "setInvoiceReceiver": "Definir destinatario de la factura",
    "reallyCreateInvoiceTitle": "¿Seguro que desea crear la factura?",
    "reallyCreateInvoiceText": "Se generará un número de factura correlativo. La factura no se puede eliminar.",
    "confirmCreateInvoice": "Crear factura"
  }
}
</i18n>
